import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import {
  ProjectResearchSubjectUpdatePayload,
  ProjectResearchSubjectDetail,
  ScheduleSubmissionUsersParams,
  ScheduleSubmissionUsersResponse,
  StudyModeModel,
  SubjectScheduleGroupCreatePayload,
  SubjectScheduleGroupCreateResponse,
  SubjectOfferingCreatePayload,
  SubjectOfferingCreateResponse,
  SubjectOfferingDetail,
  SubjectOfferingHeaderUpdatePayload,
  SubjectScheduleGroupDetail,
  SubjectScheduleGroupListParams,
  SubjectScheduleGroupListResponse,
  SubjectScheduleGroupSummary,
} from '../../models/subject-schedules/subject-schedule.model';
import { UserManagementListResponse, UserManagementModel } from '../../models/user-management/user-management.model';
import { UserManagementListParams, UserManagementService } from '../user-management/user-management.service';

const SKIP_GLOBAL_ERROR_DIALOG_HEADER = 'X-Skip-Global-Error-Dialog';

@Injectable({ providedIn: 'root' })
export class SubjectScheduleService {
  private readonly http = inject(HttpClient);
    private readonly userManagementService = inject(UserManagementService);
  
  private readonly apiUrl = '/subject_offering_groups';
  private readonly createUrl = '/subject_schedule_groups';
  private readonly detailUrl = '/subject_schedule_groups';
  private readonly projectResearchUrl = '/project_research_subjects';
  private readonly subjectOfferingsUrl = '/subject_offerings';
  private readonly scheduleSubmission = '/schedule_submissions';

  private readonly studyMode = '/study_modes';

  getStudyModes(): Observable<StudyModeModel[]> {
    return this.http.get<unknown>(this.studyMode)
      .pipe(map((response) => this.normalizeStudyModesResponse(response)));
  }

  getSubjectScheduleGroups(
    params: SubjectScheduleGroupListParams = {},
  ): Observable<SubjectScheduleGroupListResponse> {
    return this.http
      .get<unknown>(this.apiUrl, { params: this.buildParams(params) })
      .pipe(map((response) => this.normalizeGroupListResponse(response, params)));
  }

  createSubjectScheduleGroup(
    payload: SubjectScheduleGroupCreatePayload,
  ): Observable<SubjectScheduleGroupCreateResponse> {
    return this.http.post<SubjectScheduleGroupCreateResponse>(this.createUrl, payload);
  }

  createSubjectOffering(
    payload: SubjectOfferingCreatePayload,
  ): Observable<SubjectOfferingCreateResponse> {
    return this.http.post<SubjectOfferingCreateResponse>(this.subjectOfferingsUrl, payload);
  }

  getSubjectOffering(id: number | string): Observable<SubjectOfferingDetail> {
    return this.http.get<SubjectOfferingDetail>(`${this.subjectOfferingsUrl}/${id}`);
  }

  updateSubjectOfferingHeader(id: number | string, payload: SubjectOfferingHeaderUpdatePayload): Observable<SubjectOfferingDetail> {
    return this.http.patch<SubjectOfferingDetail>(`${this.subjectOfferingsUrl}/${id}/header`, payload, {
      headers: { [SKIP_GLOBAL_ERROR_DIALOG_HEADER]: 'true' },
    });
  }

  updateSubjectScheduleGroup(
    id: number | string,
    payload: SubjectScheduleGroupCreatePayload,
  ): Observable<SubjectScheduleGroupCreateResponse> {
    return this.http.put<SubjectScheduleGroupCreateResponse>(`${this.detailUrl}/${id}`, payload);
  }

  updateProjectResearchSubject(
    id: number | string,
    payload: ProjectResearchSubjectUpdatePayload,
  ): Observable<ProjectResearchSubjectDetail> {
    return this.http.patch<ProjectResearchSubjectDetail>(`${this.projectResearchUrl}/${id}`, payload);
  }

  deleteProjectResearchSubjectAssignment(
    projectResearchSubjectId: number | string,
    assignmentId: number | string,
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.projectResearchUrl}/${projectResearchSubjectId}/assignments/${assignmentId}`,
      { headers: { [SKIP_GLOBAL_ERROR_DIALOG_HEADER]: 'true' } },
    );
  }

  getUsersRolseTeacherList(params?: UserManagementListParams): Observable<UserManagementListResponse> {
      return this.http.get<unknown>(`${this.scheduleSubmission}/eligible_users`, {
        params: this.userManagementService.buildListParams(params),
      }).pipe(
        map((res) => this.normalizeUsersListResponse(res, params))
      );
    }

  getSubjectScheduleGroupDetailsBySubjectId(
    subjectId: number | string,
    filters: Pick<SubjectScheduleGroupListParams, 'academic_term_id' | 'curriculum_type_id' | 'courseunit'> = {},
  ): Observable<SubjectScheduleGroupDetail[]> {
    return this.http
      .get<unknown>(this.detailUrl, {
        params: this.buildParams({ subject_id: subjectId, ...filters, datatable: false }),
      })
      .pipe(map((response) => this.normalizeArrayResponse<SubjectScheduleGroupDetail>(response)));
  }

  getProjectResearchSubjectsBySubjectId(
    subjectId: number | string,
    filters: Pick<SubjectScheduleGroupListParams, 'academic_term_id' | 'curriculum_type_id' | 'courseunit'> = {},
  ): Observable<ProjectResearchSubjectDetail[]> {
    return this.http
      .get<unknown>(this.projectResearchUrl, {
        params: this.buildParams({ subject_id: subjectId, ...filters, datatable: false }),
      })
      .pipe(map((response) => this.normalizeArrayResponse<ProjectResearchSubjectDetail>(response)));
  }

  getScheduleSubmissionUsers(
    params: ScheduleSubmissionUsersParams = {},
  ): Observable<ScheduleSubmissionUsersResponse> {
    return this.http.get<ScheduleSubmissionUsersResponse>(`${this.scheduleSubmission}/users`, {
      params: this.buildParams(params),
    });
  }

  private buildParams(params: SubjectScheduleGroupListParams): HttpParams {
    let httpParams = new HttpParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }

      httpParams = httpParams.set(key, String(value));
    });

    return httpParams;
  }

  private normalizeStudyModesResponse(response: unknown): StudyModeModel[] {
    if (Array.isArray(response)) {
      return response as StudyModeModel[];
    }

    if (
      typeof response === 'object'
      && response !== null
      && Array.isArray((response as { data?: unknown }).data)
    ) {
      return (response as { data: StudyModeModel[] }).data;
    }

    return [];
  }

  private normalizeGroupListResponse(
    response: unknown,
    params: SubjectScheduleGroupListParams,
  ): SubjectScheduleGroupListResponse {
    if (this.isGroupListResponse(response)) {
      return {
        data: response.data,
        page: Number(response.page ?? params.page ?? 1),
        limit: Number(response.limit ?? params.limit ?? 10),
        total: Number(response.total ?? response.data.length),
        total_pages: Number(response.total_pages ?? 1),
      };
    }

    const data = Array.isArray(response) ? response as SubjectScheduleGroupSummary[] : [];
    const limit = Number(params.limit ?? 10);

    return {
      data,
      page: Number(params.page ?? 1),
      limit,
      total: data.length,
      total_pages: Math.max(1, Math.ceil(data.length / limit)),
    };
  }

  private normalizeArrayResponse<T>(response: unknown): T[] {
    if (Array.isArray(response)) {
      return response as T[];
    }

    if (
      typeof response === 'object'
      && response !== null
      && Array.isArray((response as { data?: unknown }).data)
    ) {
      return (response as { data: T[] }).data;
    }

    return [];
  }
  

  private isGroupListResponse(
    response: unknown,
  ): response is Partial<SubjectScheduleGroupListResponse> & { data: SubjectScheduleGroupSummary[] } {
    return typeof response === 'object'
      && response !== null
      && Array.isArray((response as SubjectScheduleGroupListResponse).data);
  }

    private normalizeUsersListResponse(
      res: unknown,
      params?: UserManagementListParams
    ): UserManagementListResponse {
      if (res !== null && typeof res === 'object' && !Array.isArray(res)) {
        const response = res as Record<string, unknown>;
  
        if (Array.isArray(response.data)) {
          return {
            data: response.data as UserManagementModel[],
            page: Number(response.page ?? params?.page ?? 1),
            limit: Number(response.limit ?? params?.limit ?? 10),
            total: Number(response.total ?? response.data.length),
            total_pages: Number(response.total_pages ?? 1),
          };
        }
      }
  
      const data = Array.isArray(res) ? res as UserManagementModel[] : [];
      const limit = params?.limit ?? 10;
  
      return {
        data,
        page: params?.page ?? 1,
        limit,
        total: data.length,
        total_pages: Math.max(1, Math.ceil(data.length / limit)),
      };
    }
  
}
